import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { OpalProvider, useOpal } from '@methodfi/opal-react';
import Lottie from "lottie-react";
import loaderAnimation from "../assets/lottie.json";

// Define your API base URL here
// const API_BASE_URL = 'http://127.0.0.1:8001/api'; 
// const API_BASE_URL = 'https://test-db.volafinance.com/api';
// const API_BASE_URL = 'https://opal-sandbox.volafinance.com/api';

const API_BASE_URL = "https://methodfi-new-sandbox.volafinance.com/api/v1/";

// Platform detection utility
const getPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
        return 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent) || /ios/i.test(userAgent)) {
        return 'ios';
    }
    return 'web';
};

function OpalFlowInner({ nativeData, token, onFlowFinish, setLoading }) {
    const [error, setError] = useState(null);
    const [hasOpened, setHasOpened] = useState(false);
    const retrieveAccountsPromise = useRef(null);

    // Utility to notify native app after event
    const notifyNativeApp = (payload) => {
        const platform = getPlatform();
        console.log(`Notifying native app on ${platform} with payload:`, payload.eventData.type);
        if (platform === 'ios' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.reactNative) {
            console.log('Posting message to iOS native app');
            window.webkit.messageHandlers.reactNative.postMessage({ event: 'opalEventLogged', event_type: payload.eventData.type, data: payload });
        } else if (platform === 'android' && window.ReactNativeWebView) {
            console.log('Posting message to Android native app');
            window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'opalEventLogged', event_type: payload.eventData.type, data: payload }));
        } else {
            console.log('No native app bridge found to post message');
            console.log('No native app bridge found to post message', { event: 'opalEventLogged', event_type: payload.eventData.type, data: payload });
        }
    };

    const postEvent = async (payload) => {
        if (!nativeData) return;
        try {
            await axios.post(
                // `${API_BASE_URL}/methodfi/opal/event`,
                `${API_BASE_URL}opal/event`,
                payload,
                { headers: nativeData }
            );

            // Notify native app after successful API call
            notifyNativeApp(payload);
        } catch (err) {
            console.error('Failed to log Opal event:', err);
            setError('Failed to log Opal event');
        }
    };

    // Call retrieveAccounts API
    const retrieveAccounts = async () => {
        console.log('Retrieving accounts with native data:', nativeData);
        if (!nativeData) return;
        setLoading(true);
        setError(null);
        try {
            console.log('Calling retrieveAccounts API with native data:', nativeData);
            // await axios.post(
            //     `${API_BASE_URL}/methodfi/retrieveaccounts`,
            //     {},
            //     { headers: nativeData }
            // );


         //FOR PYTHON API CALLS

             await Promise.all([
                  axios.get(
                    `${API_BASE_URL}retrieveaccounts/creditscore`,
                    { headers: nativeData }
                ),
                 axios.get(
                    `${API_BASE_URL}retrieveaccounts/connect`,
                    { headers: nativeData }
                ),
                 axios.get(
                    `${API_BASE_URL}retrieveaccounts/attribute`,
                    { headers: nativeData }
                )
             ])


            await new Promise(resolve => setTimeout(resolve, 10000));
            notifyNativeApp({
                eventData: { type: 'flow.completed' },
                userId: nativeData.userId,
                status: 'success'
            });
            onFlowFinish();
        } catch (err) {
            setError('Failed to retrieve accounts');
            console.error('Failed to retrieve accounts:', err);
        } finally {
            setLoading(false);
            retrieveAccountsPromise.current = null; // cleanup
        }
    };

    const { open } = useOpal({
        env: 'dev',
        token,
        onSuccess: async (payload) => {
            console.log('Opal success:', payload);
            setHasOpened(false);

            // If retrieveAccounts is in flight, wait for it first
            if (retrieveAccountsPromise.current) {
                await retrieveAccountsPromise.current;
            }

            // Ensure payload is always an object
            const successPayload = payload || { type: 'opal.session.success' };

            await postEvent({ userId: nativeData.userId, status: 'success', eventData: successPayload });
        },
        onExit: async (payload) => {
            console.log('Opal exit:', payload);
            setHasOpened(false);

            // Wait if retrieveAccounts is still in flight
            if (retrieveAccountsPromise.current) {
                await retrieveAccountsPromise.current;
            }
            
            // Ensure payload is always an object
            const exitPayload = payload || { type: 'opal.session.exited' };

            await postEvent({ userId: nativeData.userId, status: 'exit', eventData: exitPayload });

            // Prevent reopening: mark flow as finished in parent
            onFlowFinish();
        },
        onEvent: async (event) => {
            console.log('Opal event:', event);
            if (!event || !event.type || event.type.startsWith('webpack')) return;

            if (event.type === 'opal.session.exited'){
                console.log('Session exited, marking flow as finished');
                onFlowFinish();
            }
           
            if (event.type === 'connect.flow.completed') {
                console.log('Connect flow completed with accounts:', event.data);
                if (!retrieveAccountsPromise.current) {
                    retrieveAccountsPromise.current = retrieveAccounts();
                }
                try {
                    await retrieveAccountsPromise.current;
                } catch (err) {
                    console.error('Account retrieval failed:', err);
                } finally {
                    retrieveAccountsPromise.current = null;
                }
            }

            // Wait for retrieveAccounts if it's still in flight before posting any event
            if (retrieveAccountsPromise.current) {
                try {
                    await retrieveAccountsPromise.current;
                } catch (err) {
                    console.error('Account retrieval failed while waiting for postEvent:', err);
                }
            }

            await postEvent({ userId: nativeData.userId, status: event.action, eventData: event });
        },
    });

    useEffect(() => {
        if (token && typeof open === 'function' && !hasOpened) {
            open({ token });
            setHasOpened(true);
        }
    }, [token, open, hasOpened]);

    // Error UI
    if (error) return (
        <div className="loader-container">
            <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>
        </div>
    );
}

export default function OpalFlow() {
    const [nativeData, setNativeData] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [flowFinished, setFlowFinished] = useState(false);

    useEffect(() => {
        // Check if the handler exists before calling
        window.webkit?.messageHandlers?.onReactReady?.postMessage({
            status: "onReactLoaded",
            timestamp: Date.now()
        });
        console.log('Notified native app that React is ready');
    }, []);

    useEffect(() => {
        window.setNativeData = (data) => {
            console.log('Received native data:', data);
            if (typeof data === 'string') {
                try { data = JSON.parse(data); } catch (e) { }
            }
            setNativeData(data);
            setFlowFinished(false); // Reset if new data comes in
        };
        return () => { delete window.setNativeData; };
    }, []);

    useEffect(() => {
        if (!nativeData || flowFinished) return;
        let isMounted = true; // Prevent state updates if component unmounts
        setLoading(true);
        setError(null);
        const fetchToken = async () => {
            try {
                const response = await axios.post(
                    // `${API_BASE_URL}/methodfi/opal/session`,
                    
                    `${API_BASE_URL}opal/session`,
                    {},
                    { headers: nativeData }
                );
                //setToken(response.data.data.session_token);
                if (isMounted) setToken(response.data.data.session_token);
            } catch (err) {
                console.error('Failed to fetch session token:', err);
                // Show specific error message from API if available
                let message = 'Failed to fetch session token';
                if (err.response && err.response.data && err.response.data.message) {
                    message = err.response.data.message;
                } else if (err.message) {
                    message = err.message;
                }
                //setError(message);
                if (isMounted) setError(message);
            } finally {
                //setLoading(false);
                if (isMounted) setLoading(false);
            }
        };
        fetchToken();
        return () => { isMounted = false; };
    }, [nativeData, flowFinished]);

    // Only render OpalProvider and OpalFlowInner when both nativeData and token are available
    // Loader UI
    if (loading) return (
        // <div className="loader-container">
        //     <div className="loader"></div>
        // </div>
        <div className="loader-container">
            <Lottie animationData={loaderAnimation} style={{ width: 100, height: 100 }} />
        </div>
    );
    // Error UI
    if (error) return (
        <div className="loader-container">
            <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>
        </div>
    );
    if (!nativeData || !token || flowFinished) return null;

    return (
        <OpalProvider>
            <OpalFlowInner nativeData={nativeData} token={token} onFlowFinish={() => setFlowFinished(true)} setLoading={setLoading} />
        </OpalProvider>
    );
}